<template>
  <div class="home">
    <Navbar :is-active="6"></Navbar>
    <div class="body">
      <MesasSidebar active="api-test" />
      <div class="body__containt">
        <div class="page__title">
          <open-arrow></open-arrow>
          <div>
            <h2>Probar pago de mesa (cliente)</h2>
            <p class="page__lead">
              Arma la comanda en <router-link to="/pos-mesas">POS</router-link>,
              elige la mesa y ejecuta cada paso. A la derecha ves campos, valores y JSON juntos.
            </p>
          </div>
        </div>

        <div class="status-bar">
          <div class="status-item">
            <span>Mesa</span>
            <strong>{{ selectedTable ? '#' + selectedTable.table_number + ' ' + selectedTable.table_name : '—' }}</strong>
          </div>
          <div class="status-item">
            <span>Comanda</span>
            <strong>{{ orderId || '—' }}</strong>
          </div>
          <div class="status-item">
            <span>Pendiente</span>
            <strong>{{ orderPending != null ? 'S/ ' + formatMoney(orderPending) : '—' }}</strong>
          </div>
          <div class="status-item">
            <span>Factura</span>
            <strong>{{ invoiceId || '—' }}</strong>
          </div>
          <div class="status-item status-item--wide">
            <span>QR</span>
            <strong class="mono">{{ uuid || '—' }}</strong>
          </div>
          <button class="btn btn--accent" :disabled="!canRunFlow || busy" @click="runFullFlow">
            {{ busy ? 'Ejecutando…' : 'Correr flujo' }}
          </button>
        </div>

        <section class="prep-card">
          <div class="prep-row">
            <label class="field">
              <span>Elige mesa (con comanda en POS)</span>
              <select v-model="selectedTableId" @change="onTableChange">
                <option disabled value="">Selecciona una mesa</option>
                <option v-for="t in tables" :key="t.id" :value="t.id">
                  #{{ t.table_number }} — {{ t.table_name }}
                </option>
              </select>
            </label>
            <button class="btn btn--ghost" :disabled="!selectedTableId || busy" @click="loadOrder">
              Refrescar
            </button>
          </div>
          <p class="tip tip--ok" v-if="orderId && orderPending > 0">
            Lista · comanda #{{ orderId }} · pendiente S/ {{ formatMoney(orderPending) }}
          </p>
          <p class="tip" v-else-if="selectedTableId">
            Sin comanda pendiente. Abre platos en <router-link to="/pos-mesas">POS</router-link>.
          </p>
        </section>

        <nav class="stepper">
          <button
            v-for="s in steps"
            :key="s.id"
            type="button"
            class="stepper__item"
            :class="{ active: step === s.id, done: s.id < step }"
            @click="goStep(s.id)"
            :title="s.name"
          >
            <span class="stepper__num">{{ s.id }}</span>
            <span class="stepper__label">{{ s.short }}</span>
          </button>
        </nav>

        <section class="step-panel" v-if="currentStepMeta">
          <header class="step-head">
            <div>
              <p class="step-name">Paso {{ currentStepMeta.id }}</p>
              <h3>{{ currentStepMeta.name }}</h3>
              <p class="step-desc">{{ currentStepMeta.desc }}</p>
            </div>
            <div class="api-badge">
              <span class="api-badge__method" :class="'m-' + currentStepMeta.method.toLowerCase()">
                {{ currentStepMeta.method }}
              </span>
              <code class="api-badge__path">{{ currentStepMeta.path }}</code>
            </div>
          </header>

          <div class="step-layout">
            <!-- Izquierda: probar -->
            <aside class="try">
              <h4 class="panel__title">1. Probar</h4>

              <template v-if="step === 1">
                <label class="field">
                  <span>UUID del QR</span>
                  <input type="text" v-model="uuid" readonly />
                </label>
                <div class="btn-row">
                  <button class="btn" :disabled="!uuid || busy" @click.prevent="callGetTable">Ejecutar</button>
                  <button class="btn btn--next" :disabled="!hasStepResult(1)" @click="goStep(2)">Siguiente</button>
                </div>
              </template>

              <template v-else-if="step === 2">
                <label class="check">
                  <input type="checkbox" v-model="payAll" />
                  Pagar toda la cuenta pendiente
                </label>
                <div v-if="!payAll" class="items-pick">
                  <p class="items-pick__title">Productos a pagar</p>
                  <p class="tip" v-if="!pendingItems.length && !reservedItems.length">
                    {{ uuid ? 'Sin ítems pendientes. Arma la comanda en POS o vuelve a escanear en el paso 1.' : 'Elige una mesa y escanea en el paso 1.' }}
                  </p>
                  <p class="tip" v-else-if="!pendingItems.length && reservedItems.length">
                    Todos los ítems están reservados (prefactura activa). Espera que venza la reserva o cobra esa factura.
                  </p>
                  <label v-for="item in pendingItems" :key="item.id" class="check check--item">
                    <input type="checkbox" :value="item.id" v-model="selectedItemIds" />
                    <span>
                      #{{ item.id }} · {{ item.name }}
                      <em>x{{ item.quantity }} · S/ {{ formatMoney(item.line_total) }}</em>
                    </span>
                  </label>
                  <label v-for="item in reservedItems" :key="'r' + item.id" class="check check--item is-reserved">
                    <input type="checkbox" disabled />
                    <span>
                      #{{ item.id }} · {{ item.name }}
                      <em>reservado · x{{ item.quantity }} · S/ {{ formatMoney(item.line_total) }}</em>
                    </span>
                  </label>
                </div>
                <label class="field field--sm">
                  <span>tip_percentage</span>
                  <input type="number" min="0" max="100" v-model.number="tipPercentage" />
                </label>
                <div class="btn-row">
                  <button class="btn btn--ghost" @click="goStep(1)">Atrás</button>
                  <button
                    class="btn"
                    :disabled="!orderId || busy || (!payAll && !selectedItemIds.length)"
                    @click.prevent="callPreview"
                  >Ejecutar</button>
                  <button class="btn btn--next" :disabled="!hasStepResult(2)" @click="goStep(3)">Siguiente</button>
                </div>
              </template>

              <template v-else-if="step === 3">
                <p class="tip" v-if="!hasStepResult(2)">Primero calcula el total en el paso 2.</p>
                <label class="check">
                  <input type="checkbox" v-model="payAll" />
                  Pagar / reservar toda la cuenta pendiente
                </label>
                <div v-if="!payAll" class="items-pick">
                  <p class="items-pick__title">Productos a reservar</p>
                  <p class="tip" v-if="!pendingItems.length && !reservedItems.length">
                    {{ uuid ? 'Sin ítems pendientes. Arma la comanda en POS o vuelve a escanear en el paso 1.' : 'Elige una mesa y escanea en el paso 1.' }}
                  </p>
                  <p class="tip" v-else-if="!pendingItems.length && reservedItems.length">
                    Todos los ítems están reservados (prefactura activa). Espera que venza la reserva o cobra esa factura.
                  </p>
                  <label v-for="item in pendingItems" :key="'inv-' + item.id" class="check check--item">
                    <input type="checkbox" :value="item.id" v-model="selectedItemIds" />
                    <span>
                      #{{ item.id }} · {{ item.name }}
                      <em>x{{ item.quantity }} · S/ {{ formatMoney(item.line_total) }}</em>
                    </span>
                  </label>
                  <label v-for="item in reservedItems" :key="'inv-r' + item.id" class="check check--item is-reserved">
                    <input type="checkbox" disabled />
                    <span>
                      #{{ item.id }} · {{ item.name }}
                      <em>reservado · x{{ item.quantity }} · S/ {{ formatMoney(item.line_total) }}</em>
                    </span>
                  </label>
                </div>
                <label class="field field--sm">
                  <span>tip_percentage</span>
                  <input type="number" min="0" max="100" v-model.number="tipPercentage" />
                </label>
                <div class="guest-grid">
                  <label class="field">
                    <span>guest.name</span>
                    <input type="text" v-model="guest.name" />
                  </label>
                  <label class="field">
                    <span>guest.email</span>
                    <input type="email" v-model="guest.email" />
                  </label>
                  <label class="field">
                    <span>guest.phone</span>
                    <input type="text" v-model="guest.phone" />
                  </label>
                  <label class="field">
                    <span>guest.doc_type</span>
                    <select v-model="guest.doc_type">
                      <option value="01">01 — DNI (boleta)</option>
                      <option value="06">06 — RUC (factura)</option>
                      <option value="04">04 — CE</option>
                      <option value="07">07 — Pasaporte</option>
                    </select>
                  </label>
                  <label class="field">
                    <span>guest.doc_number</span>
                    <input type="text" v-model="guest.doc_number" placeholder="Opcional (boleta sin doc = -)" />
                  </label>
                  <label class="field">
                    <span>guest.address</span>
                    <input type="text" v-model="guest.address" placeholder="Dirección (factura)" />
                  </label>
                </div>
                <div class="btn-row">
                  <button class="btn btn--ghost" @click="goStep(2)">Atrás</button>
                  <button
                    class="btn"
                    :disabled="!orderId || !hasStepResult(2) || busy || (!payAll && !selectedItemIds.length)"
                    @click.prevent="callCreateInvoice"
                  >
                    Ejecutar
                  </button>
                  <button class="btn btn--next" :disabled="!invoiceId" @click="goStep(4)">Siguiente</button>
                </div>
              </template>

              <template v-else-if="step === 4">
                <label class="field">
                  <span>card_token</span>
                  <input type="text" v-model="cardToken" />
                </label>
                <div class="btn-row">
                  <button class="btn btn--ghost" @click="goStep(3)">Atrás</button>
                  <button class="btn btn--accent" :disabled="!invoiceId || busy" @click.prevent="callCharge">Ejecutar</button>
                  <button class="btn btn--next" :disabled="!hasStepResult(4)" @click="goStep(5)">Siguiente</button>
                </div>
              </template>

              <template v-else-if="step === 5">
                <p class="tip" v-if="!canUseReceipt">Ejecuta el cobro en el paso 4 para obtener token y URLs.</p>
                <div class="btn-row">
                  <button class="btn btn--ghost" @click="goStep(4)">Atrás</button>
                  <button class="btn" :disabled="!canUseReceipt || busy" @click.prevent="callViewReceipt">Ver HTML</button>
                  <button class="btn btn--accent" :disabled="!canUseReceipt || busy" @click.prevent="callDownloadPdf">
                    Descargar PDF
                  </button>
                  <button class="btn btn--next" @click="goStep(6)">Siguiente</button>
                </div>
              </template>

              <template v-else-if="step === 6">
                <div class="guest-grid">
                  <label class="field">
                    <span>score restaurante</span>
                    <input type="number" min="1" max="5" v-model.number="ratings.restaurant" />
                  </label>
                  <label class="field">
                    <span>score mesero</span>
                    <input type="number" min="1" max="5" v-model.number="ratings.waiter" />
                  </label>
                  <label class="field">
                    <span>comment</span>
                    <input type="text" v-model="ratings.comment" />
                  </label>
                </div>
                <div class="btn-row">
                  <button class="btn btn--ghost" @click="goStep(5)">Atrás</button>
                  <button class="btn" :disabled="!orderId || busy" @click.prevent="callRating">Ejecutar</button>
                  <button class="btn btn--accent" :disabled="!canRunFlow || busy" @click="runFullFlow">
                    Repetir flujo
                  </button>
                </div>
              </template>
            </aside>

            <!-- Derecha: respuesta unificada -->
            <section class="reply">
              <div class="reply__tabs">
                <button
                  type="button"
                  :class="{ active: replyTab === 'campos' }"
                  @click="replyTab = 'campos'"
                >Campos y valores</button>
                <button
                  type="button"
                  :class="{ active: replyTab === 'json' }"
                  @click="replyTab = 'json'"
                >JSON</button>
                <button
                  type="button"
                  :class="{ active: replyTab === 'request' }"
                  @click="replyTab = 'request'"
                >Qué enviar</button>
              </div>

              <div class="reply__body">
                <div
                  v-if="resultBox.show"
                  class="result-banner"
                  :class="resultBox.ok ? 'is-ok' : 'is-err'"
                >
                  <strong>{{ resultBox.title }}</strong>
                  <span>{{ resultBox.meta }}</span>
                </div>
                <div v-else class="result-banner is-empty">
                  <strong>Sin respuesta aún</strong>
                  <span>Pulsa <em>Ejecutar</em> a la izquierda para ver el resultado aquí.</span>
                </div>

                <div v-if="replyTab === 'campos'" class="guide">
                  <div v-for="(group, gi) in groupedResponse" :key="'g' + gi" class="guide__group">
                    <h5 v-if="group.title">{{ group.title }}</h5>
                    <div
                      v-for="(p, i) in group.items"
                      :key="'f' + gi + '-' + i"
                      class="guide__row"
                    >
                      <code>{{ p.key }}</code>
                      <div class="guide__meta">
                        <strong class="guide__val" :class="{ muted: !hasValue(p.key) }">
                          {{ displayValue(p.key) }}
                        </strong>
                        <span class="guide__desc">{{ p.desc }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-else-if="replyTab === 'json'" class="json-box">
                  <pre v-if="resultBox.show && resultBox.json">{{ resultBox.json }}</pre>
                  <p v-else class="empty-result">Aún no hay JSON. Ejecuta el servicio.</p>
                </div>

                <div v-else class="guide">
                  <div v-for="(group, gi) in groupedRequest" :key="'rq' + gi" class="guide__group">
                    <h5 v-if="group.title">{{ group.title }}</h5>
                    <div
                      v-for="(p, i) in group.items"
                      :key="'q' + gi + '-' + i"
                      class="guide__row guide__row--req"
                    >
                      <code>{{ p.key }}</code>
                      <span class="guide__desc">{{ p.desc }}</span>
                    </div>
                  </div>

                  <div class="request-json">
                    <h5>JSON a enviar</h5>
                    <p class="request-json__meta" v-if="outgoingRequest.meta">{{ outgoingRequest.meta }}</p>
                    <div class="json-box">
                      <pre v-if="outgoingRequest.json">{{ outgoingRequest.json }}</pre>
                      <p v-else class="empty-result">Este paso no envía body (solo path / query).</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Navbar from '@/components/NavBar.vue'
import MesasSidebar from '@/components/MesasSidebar.vue'
import OpenArrow from '@/components/OpenArrow.vue'

const STEP_DOCS = [
  {
    id: 1,
    short: 'Escanear QR',
    name: 'Escanear QR / ver cuenta',
    desc: 'Carga mesa, config del local y la comanda actual (sin historial).',
    method: 'GET',
    path: '/api/v1/table/{uuid}',
    request: [
      { group: 'Path', key: 'uuid', desc: 'Token del QR de la mesa (table_uuid). Va en la URL.' }
    ],
    response: [
      { group: 'Raíz', key: 'success', desc: 'true = llamada exitosa · false = falló.' },
      { group: 'Raíz', key: 'paid_fully', desc: 'true = no hay pendiente de pagar · false = aún hay cuenta pendiente.' },
      { group: 'Raíz', key: 'message', desc: 'Texto legible del estado (ej. “OK”, “Mesa sin comanda abierta.”).' },
      { group: 'table', key: 'table.id', desc: 'ID interno de la mesa.' },
      { group: 'table', key: 'table.uuid', desc: 'UUID del QR (mismo valor del path).' },
      { group: 'table', key: 'table.number', desc: 'Número de mesa (ej. 1).' },
      { group: 'table', key: 'table.name', desc: 'Nombre visible (ej. “mesa 01”).' },
      { group: 'restaurant', key: 'restaurant.company_id', desc: 'Empresa dueña de la mesa.' },
      { group: 'restaurant', key: 'restaurant.igv_percentage', desc: '% de IGV (ej. 18).' },
      { group: 'restaurant', key: 'restaurant.tip_enabled', desc: 'true = se puede elegir propina · false = propina deshabilitada.' },
      { group: 'restaurant', key: 'restaurant.tip_default_percentage', desc: 'Propina sugerida por defecto (ej. 10).' },
      { group: 'restaurant', key: 'restaurant.tip_options', desc: 'Array de opciones de propina para la UI (ej. [5, 10, 15]).' },
      { group: 'restaurant', key: 'restaurant.platform_fee_percentage', desc: 'Comisión de plataforma (ej. 2.5).' },
      { group: 'restaurant', key: 'restaurant.prices_include_igv', desc: 'true = precios del menú ya incluyen IGV · false = IGV se suma aparte.' },
      { group: 'restaurant', key: 'restaurant.payment_mode', desc: 'simulate = pago simulado · (otros modos cuando haya integración real).' },
      { group: 'order', key: 'order', desc: 'null = sin comanda abierta · objeto = comanda actual con id, status y opened_at.' },
      { group: 'order', key: 'order.id', desc: 'ID de la comanda actual (open o partial).' },
      { group: 'order', key: 'order.status', desc: 'open = comanda abierta · partial = pago parcial.' },
      { group: 'order', key: 'order.opened_at', desc: 'Fecha/hora de apertura (ISO 8601).' },
      { group: 'Ítems', key: 'items_pending', desc: 'Platos aún por pagar (pending) o reservados en un pago en curso (reserved).' },
      { group: 'Ítems', key: 'items_paid', desc: 'Platos ya pagados de ESTA comanda (split), no historial viejo.' },
      { group: 'Ítem', key: 'items[].id', desc: 'ID del ítem de comanda (en items_pending / items_paid).' },
      { group: 'Ítem', key: 'items[].menu_product_id', desc: 'ID del producto del menú.' },
      { group: 'Ítem', key: 'items[].name', desc: 'Nombre del plato (snapshot).' },
      { group: 'Ítem', key: 'items[].unit_price', desc: 'Precio unitario.' },
      { group: 'Ítem', key: 'items[].quantity', desc: 'Cantidad.' },
      { group: 'Ítem', key: 'items[].line_total', desc: 'Total de la línea (precio × cantidad).' },
      { group: 'Ítem', key: 'items[].status', desc: 'pending = por pagar · reserved = reservado · paid = pagado.' },
      { group: 'Ítem', key: 'items[].reserved_until', desc: 'Solo si status=reserved: vencimiento de la reserva · null si no aplica.' },
      { group: 'Totales', key: 'totals.pending', desc: 'Suma pendiente en PEN.' },
      { group: 'Totales', key: 'totals.paid', desc: 'Suma ya pagada en esta comanda (PEN).' },
      { group: 'Totales', key: 'totals.currency', desc: 'Moneda (PEN).' }
    ]
  },
  {
    id: 2,
    short: 'Calcular',
    name: 'Calcular total (propina)',
    desc: 'Calcula subtotal, IGV, propina y comisión con los productos elegidos. No reserva ni cobra.',
    method: 'POST',
    path: '/api/v1/table/order/{orderId}/invoice-preview',
    request: [
      { group: 'Path', key: 'orderId', desc: 'ID de la comanda abierta.' },
      { group: 'Body', key: 'item_ids', desc: 'Array de IDs de ítems a pagar. Obligatorio si pay_all no es true.' },
      { group: 'Body', key: 'productos', desc: 'Alias de item_ids (opcional).' },
      { group: 'Body', key: 'pay_all', desc: 'true = calcular toda la cuenta pendiente · false = solo los item_ids enviados.' },
      { group: 'Body', key: 'tip_percentage', desc: '% de propina (ej. 10).' },
      { group: 'Body', key: 'tip_amount', desc: 'Propina en monto fijo (opcional; tiene prioridad sobre %).' },
      { group: 'Body', key: 'propina', desc: 'Alias de tip_amount si no envías tip_percentage/tip_amount.' }
    ],
    response: [
      { group: 'Raíz', key: 'success', desc: 'true = preview OK · false = error.' },
      { group: 'Raíz', key: 'message', desc: 'Mensaje de confirmación.' },
      { group: 'data', key: 'data.items_gross', desc: 'Suma bruta de los productos seleccionados.' },
      { group: 'data', key: 'data.subtotal', desc: 'Base imponible.' },
      { group: 'data', key: 'data.igv_amount', desc: 'Monto de IGV.' },
      { group: 'data', key: 'data.igv_percentage', desc: '% de IGV usado.' },
      { group: 'data', key: 'data.tip_amount', desc: 'Monto de propina calculado.' },
      { group: 'data', key: 'data.tip_percentage', desc: '% de propina aplicado.' },
      { group: 'data', key: 'data.platform_fee', desc: 'Comisión de plataforma.' },
      { group: 'data', key: 'data.platform_fee_percentage', desc: '% de comisión usado.' },
      { group: 'data', key: 'data.total', desc: 'Total a pagar.' },
      { group: 'data', key: 'data.prices_include_igv', desc: 'true = precios incluyen IGV · false = IGV se suma aparte.' },
      { group: 'data', key: 'data.currency', desc: 'Moneda (PEN).' },
      { group: 'data', key: 'data.pay_all', desc: 'true = se calculó toda la cuenta · false = solo ítems seleccionados.' },
      { group: 'data', key: 'data.selected_item_ids', desc: 'IDs de ítems incluidos.' },
      { group: 'data', key: 'data.items', desc: 'Detalle de ítems seleccionados.' },
      { group: 'data', key: 'data.items_count', desc: 'Cantidad de ítems seleccionados.' },
      { group: 'Ítem', key: 'data.items[].id', desc: 'ID del ítem.' },
      { group: 'Ítem', key: 'data.items[].menu_product_id', desc: 'Producto del menú.' },
      { group: 'Ítem', key: 'data.items[].name', desc: 'Nombre del plato.' },
      { group: 'Ítem', key: 'data.items[].unit_price', desc: 'Precio unitario.' },
      { group: 'Ítem', key: 'data.items[].quantity', desc: 'Cantidad.' },
      { group: 'Ítem', key: 'data.items[].line_total', desc: 'Total de la línea.' },
      { group: 'Ítem', key: 'data.items[].status', desc: 'pending = por pagar · reserved = reservado · paid = pagado.' }
    ]
  },
  {
    id: 3,
    short: 'Prefactura',
    name: 'Crear prefactura (reserva)',
    desc: 'Crea la prefactura y reserva los platos 5 minutos. Aún no es el comprobante fiscal.',
    method: 'POST',
    path: '/api/v1/table/order/{orderId}/invoice',
    request: [
      { group: 'Path', key: 'orderId', desc: 'ID de la comanda.' },
      { group: 'Body', key: 'item_ids', desc: 'IDs a reservar/pagar. Obligatorio si pay_all no es true.' },
      { group: 'Body', key: 'productos', desc: 'Alias de item_ids.' },
      { group: 'Body', key: 'pay_all', desc: 'true = reservar todos los pendientes · false = solo los item_ids enviados.' },
      { group: 'Body', key: 'tip_percentage', desc: '% de propina a aplicar.' },
      { group: 'Body', key: 'tip_amount', desc: 'Propina en monto fijo (opcional).' },
      { group: 'Body', key: 'propina', desc: 'Alias de tip_amount.' },
      { group: 'Body', key: 'guest.name', desc: 'Nombre del comensal.' },
      { group: 'Body', key: 'guest.email', desc: 'Email del comensal.' },
      { group: 'Body', key: 'guest.phone', desc: 'Teléfono del comensal.' },
      { group: 'Body', key: 'guest.doc_type', desc: '01 DNI · 06 RUC (factura) · 04 CE · 07 Pasaporte.' },
      { group: 'Body', key: 'guest.doc_number', desc: 'Número de documento (RUC obligatorio para factura).' },
      { group: 'Body', key: 'guest.address', desc: 'Dirección del cliente (opcional).' },
      { group: 'Body', key: 'guest_name', desc: 'Alias plano de guest.name.' },
      { group: 'Body', key: 'guest_email', desc: 'Alias plano de guest.email.' },
      { group: 'Body', key: 'guest_phone', desc: 'Alias plano de guest.phone.' }
    ],
    response: [
      { group: 'Raíz', key: 'success', desc: 'true = prefactura creada · false = error.' },
      { group: 'Raíz', key: 'message', desc: 'Mensaje (el CPE sale después del pago).' },
      { group: 'data', key: 'data.invoice_id', desc: 'ID de la prefactura (úsalo en charge).' },
      { group: 'data', key: 'data.order_id', desc: 'ID de la comanda.' },
      { group: 'data', key: 'data.total', desc: 'Total reservado a cobrar.' },
      { group: 'data', key: 'data.breakdown', desc: 'Desglose completo del preview.' },
      { group: 'data', key: 'data.breakdown.subtotal', desc: 'Base imponible.' },
      { group: 'data', key: 'data.breakdown.igv_amount', desc: 'IGV.' },
      { group: 'data', key: 'data.breakdown.tip_amount', desc: 'Propina.' },
      { group: 'data', key: 'data.breakdown.platform_fee', desc: 'Comisión.' },
      { group: 'data', key: 'data.breakdown.total', desc: 'Total.' },
      { group: 'data', key: 'data.reserved_until', desc: 'Vencimiento de la reserva (~5 min).' },
      { group: 'data', key: 'data.payment_mode', desc: 'simulate = pago simulado · otros modos con integración real.' },
      { group: 'data', key: 'data.items', desc: 'Ítems reservados.' }
    ]
  },
  {
    id: 4,
    short: 'Cobrar',
    name: 'Realizar el pago',
    desc: 'Cobra la prefactura (pago simulado) y emite CPE vía Nubefact si está activo.',
    method: 'POST',
    path: '/api/v1/table/invoice/{invoiceId}/charge',
    request: [
      { group: 'Path', key: 'invoiceId', desc: 'ID de la prefactura del paso 3.' },
      { group: 'Body', key: 'card_token', desc: 'Token de tarjeta (demo).' },
      { group: 'Body', key: 'guest.doc_type', desc: 'Opcional: actualizar tipo doc al cobrar.' },
      { group: 'Body', key: 'guest.doc_number', desc: 'Opcional: actualizar número doc al cobrar.' }
    ],
    response: [
      { group: 'Raíz', key: 'success', desc: 'true = cobro confirmado · false = error.' },
      { group: 'Raíz', key: 'message', desc: 'Mensaje de confirmación.' },
      { group: 'data', key: 'data.invoice_id', desc: 'ID de la factura.' },
      { group: 'data', key: 'data.status', desc: 'paid = pagada · reserved = reservada · pending = pendiente.' },
      { group: 'data', key: 'data.total', desc: 'Monto cobrado.' },
      { group: 'data', key: 'data.paid_at', desc: 'Fecha/hora del pago · null si aún no pagó.' },
      { group: 'data', key: 'data.order_status', desc: 'open = abierta · partial = pago parcial · paid = liquidada.' },
      { group: 'payment', key: 'data.payment.success', desc: 'true = gateway OK · false = gateway falló.' },
      { group: 'payment', key: 'data.payment.mode', desc: 'simulate = simulado.' },
      { group: 'payment', key: 'data.payment.provider', desc: 'niubiz_mock (placeholder).' },
      { group: 'payment', key: 'data.payment.transaction_id', desc: 'Referencia del pago.' },
      { group: 'payment', key: 'data.payment.amount', desc: 'Monto cobrado.' },
      { group: 'payment', key: 'data.payment.currency', desc: 'PEN.' },
      { group: 'payment', key: 'data.payment.message', desc: 'Mensaje del gateway.' },
      { group: 'billing', key: 'data.billing.success', desc: 'true = CPE emitido · false = falló emisión.' },
      { group: 'billing', key: 'data.billing.mode', desc: 'live = Nubefact · simulate = mock (Nubefact off).' },
      { group: 'billing', key: 'data.billing.provider', desc: 'nubefact o nubefact_mock.' },
      { group: 'billing', key: 'data.billing.document_id', desc: 'Serie-número o ID del CPE.' },
      { group: 'billing', key: 'data.billing.serie', desc: 'Serie (ej. BBB1 / FFF1).' },
      { group: 'billing', key: 'data.billing.numero', desc: 'Número del comprobante.' },
      { group: 'billing', key: 'data.billing.document_label', desc: 'Etiqueta serie-número.' },
      { group: 'billing', key: 'data.billing.cpe_pdf', desc: 'URL PDF de Nubefact (si live).' },
      { group: 'billing', key: 'data.billing.cpe_enlace', desc: 'URL del comprobante en Nubefact.' },
      { group: 'URLs', key: 'data.receipt_url', desc: 'URL HTML del comprobante (+ token).' },
      { group: 'URLs', key: 'data.pdf_url', desc: 'PDF Nubefact o PDF local.' }
    ]
  },
  {
    id: 5,
    short: 'Comprobante',
    name: 'Ver / descargar comprobante',
    desc: 'Consume las URLs del charge. El pago ya quedó confirmado en el paso anterior.',
    method: 'GET',
    path: '/api/v1/table/invoice/{invoiceId}/receipt[ /pdf ]?token=…',
    request: [
      { group: 'Path', key: 'invoiceId', desc: 'ID de la factura pagada.' },
      { group: 'Query', key: 'token', desc: 'Token de acceso del charge.' },
      { group: 'Rutas', key: 'GET …/receipt', desc: 'HTML del comprobante.' },
      { group: 'Rutas', key: 'GET …/receipt/pdf', desc: 'PDF para descargar.' }
    ],
    response: [
      { group: 'HTML', key: 'contenido', desc: 'Página HTML del comprobante.' },
      { group: 'PDF', key: 'application/pdf', desc: 'Archivo PDF descargable.' },
      { group: 'Desde charge', key: 'data.receipt_url', desc: 'URL para ver (viene del paso 4).' },
      { group: 'Desde charge', key: 'data.pdf_url', desc: 'URL para descargar (viene del paso 4).' },
      { group: 'Errores', key: '403', desc: 'Token inválido.' },
      { group: 'Errores', key: '400', desc: 'Factura aún no pagada.' },
      { group: 'Errores', key: '404', desc: 'No encontrado.' }
    ]
  },
  {
    id: 6,
    short: 'Calificar',
    name: 'Calificar la experiencia',
    desc: 'Opcional. Guarda puntuación de restaurante y/o mesero.',
    method: 'POST',
    path: '/api/v1/table/order/{orderId}/rating',
    request: [
      { group: 'Path', key: 'orderId', desc: 'ID de la comanda.' },
      { group: 'Body', key: 'ratings', desc: 'Array de calificaciones (mínimo una).' },
      { group: 'Body', key: 'ratings[].type', desc: 'restaurant = califica local · waiter = califica mesero.' },
      { group: 'Body', key: 'ratings[].score', desc: 'Entero 1–5 (1 = peor · 5 = mejor).' },
      { group: 'Body', key: 'ratings[].comment', desc: 'Comentario opcional · null/vacío si no hay.' },
      { group: 'Body', key: 'guest_name', desc: 'Nombre del comensal (opcional).' },
      { group: 'Body', key: 'invoice_id', desc: 'Factura asociada (opcional) · null si no aplica.' }
    ],
    response: [
      { group: 'Raíz', key: 'success', desc: 'true = calificaciones guardadas · false = error.' },
      { group: 'Raíz', key: 'message', desc: 'Confirmación.' },
      { group: 'data', key: 'data', desc: 'Lista de ratings creados.' },
      { group: 'data', key: 'data[].id', desc: 'ID del rating.' },
      { group: 'data', key: 'data[].type', desc: 'restaurant = local · waiter = mesero.' },
      { group: 'data', key: 'data[].score', desc: 'Puntaje 1–5 (1 = peor · 5 = mejor).' },
      { group: 'data', key: 'data[].comment', desc: 'Comentario · null si no enviaron.' }
    ]
  }
]

export default {
  name: 'PosApiTest',
  components: { Navbar, MesasSidebar, OpenArrow },
  data() {
    return {
      step: 1,
      replyTab: 'json',
      steps: STEP_DOCS,
      stepPayloads: {},
      resultBox: {
        show: false,
        ok: false,
        title: '',
        meta: '',
        json: '',
        data: null,
        step: null
      },
      tables: [],
      selectedTableId: '',
      selectedTable: null,
      uuid: '',
      orderId: null,
      orderPending: null,
      invoiceId: null,
      tipPercentage: 10,
      payAll: true,
      selectedItemIds: [],
      pendingItems: [],
      reservedItems: [],
      previewData: null,
      guest: {
        name: 'Invitado Test',
        email: 'invitado@test.com',
        phone: '999999999',
        doc_type: '01',
        doc_number: '',
        address: ''
      },
      cardToken: 'tok_sim_test',
      ratings: { restaurant: 5, waiter: 4, comment: 'Buena experiencia (test)' },
      paymentConfirm: null,
      receiptOpened: false,
      busy: false
    }
  },
  computed: {
    companyId() {
      return parseInt(localStorage.getItem('company_id'), 10)
    },
    itemIds() {
      return this.selectedItemIds.slice()
    },
    canRunFlow() {
      return !!(this.orderId && this.uuid && this.orderPending > 0)
    },
    currentStepMeta() {
      return this.steps.find((s) => s.id === this.step) || null
    },
    currentPayload() {
      if (this.resultBox.show && this.resultBox.step === this.step) {
        return this.resultBox.data
      }
      const saved = this.stepPayloads[String(this.step)]
      return saved || null
    },
    groupedRequest() {
      return this.groupParams((this.currentStepMeta && this.currentStepMeta.request) || [])
    },
    groupedResponse() {
      return this.groupParams((this.currentStepMeta && this.currentStepMeta.response) || [])
    },
    receiptToken() {
      const url = (this.paymentConfirm && this.paymentConfirm.receipt_url) || (this.paymentConfirm && this.paymentConfirm.pdf_url) || ''
      try {
        return new URL(url, window.location.origin).searchParams.get('token') || ''
      } catch (e) {
        return ''
      }
    },
    canUseReceipt() {
      return !!(this.invoiceId && this.receiptToken)
    },
    receiptApiPath() {
      if (!this.canUseReceipt) return ''
      return 'v1/table/invoice/' + this.invoiceId + '/receipt?token=' + encodeURIComponent(this.receiptToken)
    },
    receiptPdfApiPath() {
      if (!this.canUseReceipt) return ''
      return 'v1/table/invoice/' + this.invoiceId + '/receipt/pdf?token=' + encodeURIComponent(this.receiptToken)
    },
    outgoingRequest() {
      const built = this.buildOutgoingPayload(this.step)
      let json = ''
      if (built.body !== undefined && built.body !== null) {
        try {
          json = JSON.stringify(built.body, null, 2)
        } catch (e) {
          json = String(built.body)
        }
      }
      return {
        meta: (built.method || '') + ' ' + (built.url || ''),
        json: json,
        hasBody: built.body !== undefined && built.body !== null
      }
    }
  },
  created() {
    if (!localStorage.getItem('token')) {
      this.$router.push('/login')
      return
    }
    this.loadTables()
  },
  methods: {
    hasStepResult(id) {
      return !!this.stepPayloads[String(id)]
    },
    emptyResultBox() {
      return {
        show: false,
        ok: false,
        title: '',
        meta: '',
        json: '',
        data: null,
        step: null
      }
    },
    showResultForStep(id) {
      const saved = this.stepPayloads[String(id)]
      if (!saved) {
        this.resultBox = this.emptyResultBox()
        return
      }
      this.resultBox = {
        show: true,
        ok: saved.__ok !== false,
        title: saved.__ok !== false ? 'Respuesta recibida' : 'Error en la llamada',
        meta: (saved.__method || '—') + ' ' + (saved.__url || '—') + ' · HTTP ' + (saved.__httpStatus || '—'),
        json: saved.__json || JSON.stringify(saved.__raw != null ? saved.__raw : saved, null, 2),
        data: saved.__raw != null ? saved.__raw : saved,
        step: id
      }
    },
    goStep(id) {
      this.step = id
      this.replyTab = 'json'
      this.showResultForStep(id)
      if ((id === 2 || id === 3) && this.uuid && !this.pendingItems.length && !this.reservedItems.length) {
        this.refreshPendingItems()
      }
    },
    groupParams(list) {
      const groups = []
      let current = null
      list.forEach((item) => {
        const title = item.group || ''
        if (!current || current.title !== title) {
          current = { title, items: [] }
          groups.push(current)
        }
        current.items.push(item)
      })
      return groups
    },
    formatMoney(v) {
      return (Math.round((Number(v) + Number.EPSILON) * 100) / 100).toFixed(2)
    },
    buildOutgoingPayload(stepId) {
      if (stepId === 1) {
        return {
          method: 'GET',
          url: '/api/v1/table/' + (this.uuid || '{uuid}'),
          body: null
        }
      }
      if (stepId === 2) {
        const body = { tip_percentage: this.tipPercentage, pay_all: this.payAll }
        if (!this.payAll) body.item_ids = this.itemIds
        return {
          method: 'POST',
          url: '/api/v1/table/order/' + (this.orderId || '{orderId}') + '/invoice-preview',
          body: body
        }
      }
      if (stepId === 3) {
        const body = {
          tip_percentage: this.tipPercentage,
          pay_all: this.payAll,
          guest: Object.assign({}, this.guest)
        }
        if (!this.payAll) body.item_ids = this.itemIds
        return {
          method: 'POST',
          url: '/api/v1/table/order/' + (this.orderId || '{orderId}') + '/invoice',
          body: body
        }
      }
      if (stepId === 4) {
        return {
          method: 'POST',
          url: '/api/v1/table/invoice/' + (this.invoiceId || '{invoiceId}') + '/charge',
          body: { card_token: this.cardToken || null }
        }
      }
      if (stepId === 5) {
        return {
          method: 'GET',
          url: this.receiptApiPath
            ? '/api/' + this.receiptApiPath
            : '/api/v1/table/invoice/{invoiceId}/receipt?token={token}',
          body: null
        }
      }
      if (stepId === 6) {
        const ratings = []
        if (this.ratings.restaurant) {
          ratings.push({ type: 'restaurant', score: this.ratings.restaurant, comment: this.ratings.comment })
        }
        if (this.ratings.waiter) {
          ratings.push({ type: 'waiter', score: this.ratings.waiter, comment: this.ratings.comment })
        }
        return {
          method: 'POST',
          url: '/api/v1/table/order/' + (this.orderId || '{orderId}') + '/rating',
          body: {
            ratings: ratings,
            guest_name: this.guest.name,
            invoice_id: this.invoiceId || null
          }
        }
      }
      return { method: '', url: '', body: null }
    },
    saveStepResult(stepId, method, url, status, data, ok) {
      let jsonText = ''
      try {
        jsonText = JSON.stringify(data, null, 2)
      } catch (e) {
        jsonText = String(data)
      }

      const stored = {
        __raw: data,
        __json: jsonText,
        __ok: !!ok,
        __httpStatus: status,
        __method: method,
        __url: url
      }

      // Copia plana de campos top-level para resolver paths en "Campos y valores"
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        Object.keys(data).forEach((k) => {
          stored[k] = data[k]
        })
      }

      // Vue 3: no existe $set; asignar la clave basta (Proxy reactivo)
      this.stepPayloads[String(stepId)] = stored

      this.resultBox = {
        show: true,
        ok: !!ok,
        title: ok ? 'Respuesta recibida' : 'Error en la llamada',
        meta: method + ' ' + url + ' · HTTP ' + status,
        json: jsonText,
        data: data,
        step: stepId
      }
      this.replyTab = 'json'
    },
    getByPath(obj, path) {
      if (!obj || !path) return undefined
      if (path.indexOf('[]') !== -1) return undefined
      const parts = path.split('.')
      let cur = obj
      for (let i = 0; i < parts.length; i++) {
        if (cur == null) return undefined
        cur = cur[parts[i]]
      }
      return cur
    },
    hasValue(path) {
      return this.resolveField(path) !== undefined
    },
    resolveField(path) {
      const payload = this.currentPayload
      if (!payload) {
        if (this.step === 5 && this.stepPayloads['4']) {
          return this.getByPath(this.stepPayloads['4'].__raw || this.stepPayloads['4'], path)
        }
        return undefined
      }
      const source = payload.__raw || payload
      let val = this.getByPath(source, path)
      if (val === undefined && path.indexOf('data.') === 0) {
        val = this.getByPath(source, path.slice(5))
      }
      return val
    },
    displayValue(path) {
      const val = this.resolveField(path)
      if (val === undefined) return '—'
      if (val === null) return 'null'
      if (typeof val === 'boolean') return val ? 'true' : 'false'
      if (typeof val === 'object') {
        try {
          const s = JSON.stringify(val)
          return s.length > 160 ? s.slice(0, 157) + '…' : s
        } catch (e) {
          return String(val)
        }
      }
      return String(val)
    },
    applyAccountSummary(data) {
      if (!data || !data.success) {
        this.pendingItems = []
        this.reservedItems = []
        return
      }
      const all = Array.isArray(data.items_pending) ? data.items_pending : []
      this.pendingItems = all.filter(function (i) {
        return !i.status || i.status === 'pending'
      })
      this.reservedItems = all.filter(function (i) {
        return i.status === 'reserved'
      })
      if (!this.selectedItemIds.length) {
        this.selectedItemIds = this.pendingItems.map(function (i) { return i.id })
      } else {
        const valid = {}
        this.pendingItems.forEach(function (i) { valid[i.id] = true })
        this.selectedItemIds = this.selectedItemIds.filter(function (id) { return valid[id] })
      }
      if (data.order && data.order.id) this.orderId = data.order.id
      if (data.totals && data.totals.pending != null) this.orderPending = data.totals.pending
    },
    async refreshPendingItems() {
      if (!this.uuid) return
      try {
        const res = await axios.get('v1/table/' + this.uuid)
        this.applyAccountSummary(res.data)
      } catch (e) {
        // silencioso: el usuario puede escanear en el paso 1
      }
    },
    async loadTables() {
      try {
        const res = await axios.get('general/table', {
          params: { page: 1, table_company_id: this.companyId }
        })
        this.tables = res.data.export || (res.data.data && res.data.data.data) || []
      } catch (e) {
        this.tables = []
      }
    },
    onTableChange() {
      const id = Number(this.selectedTableId)
      this.selectedTable = this.tables.find(function (t) { return Number(t.id) === id }) || null
      this.uuid = this.selectedTable ? this.selectedTable.table_uuid : ''
      this.orderId = null
      this.orderPending = null
      this.invoiceId = null
      this.paymentConfirm = null
      this.receiptOpened = false
      this.pendingItems = []
      this.reservedItems = []
      this.selectedItemIds = []
      this.previewData = null
      this.stepPayloads = {}
      this.resultBox = this.emptyResultBox()
      this.replyTab = 'json'
      if (id) this.loadOrder()
    },
    async request(method, url, payload, stepId) {
      this.busy = true
      try {
        const config = {
          method: method,
          url: url
        }
        if (method === 'get') {
          if (payload !== undefined && payload !== null) config.params = payload
        } else {
          config.data = payload
        }
        const res = await axios(config)
        if (stepId != null) {
          this.saveStepResult(stepId, method.toUpperCase(), url, res.status, res.data, true)
        }
        return res.data
      } catch (e) {
        const status = (e.response && e.response.status) || 0
        const data = (e.response && e.response.data) || { message: e.message || 'Error de red' }
        if (stepId != null) {
          this.saveStepResult(stepId, method.toUpperCase(), url, status, data, false)
        }
        return null
      } finally {
        this.busy = false
      }
    },
    async loadOrder() {
      const data = await this.request('get', 'general/table/' + this.selectedTableId + '/order', {
        company_id: this.companyId
      }, null)
      if (data && data.success && data.data) {
        this.orderId = data.data.id
        this.orderPending = (data.data.totals && data.data.totals.pending != null) ? data.data.totals.pending : 0
      } else {
        this.orderId = null
        this.orderPending = null
      }
      await this.refreshPendingItems()
    },
    async callGetTable() {
      try {
        const data = await this.request('get', 'v1/table/' + this.uuid, null, 1)
        this.applyAccountSummary(data)
        return data
      } catch (e) {
        this.saveStepResult(1, 'GET', 'v1/table/' + this.uuid, 0, { message: e.message || 'Error' }, false)
        return null
      }
    },
    async callPreview() {
      const body = { tip_percentage: this.tipPercentage, pay_all: this.payAll }
      if (!this.payAll) body.item_ids = this.itemIds
      const data = await this.request('post', 'v1/table/order/' + this.orderId + '/invoice-preview', body, 2)
      this.previewData = (data && data.success) ? data.data : null
    },
    async callCreateInvoice() {
      const body = {
        tip_percentage: this.tipPercentage,
        pay_all: this.payAll,
        guest: Object.assign({}, this.guest)
      }
      if (!this.payAll) body.item_ids = this.itemIds
      const data = await this.request('post', 'v1/table/order/' + this.orderId + '/invoice', body, 3)
      if (data && data.success && data.data) {
        this.invoiceId = data.data.invoice_id
        if (data.data.breakdown) this.previewData = data.data.breakdown
      }
      await this.refreshPendingItems()
    },
    async callCharge() {
      const data = await this.request(
        'post',
        'v1/table/invoice/' + this.invoiceId + '/charge',
        { card_token: this.cardToken || null },
        4
      )
      this.receiptOpened = false
      if (data && data.success && data.data) {
        this.paymentConfirm = {
          invoice_id: data.data.invoice_id,
          status: data.data.status,
          total: data.data.total,
          payment_ref: data.data.payment && data.data.payment.transaction_id,
          billing_ref: data.data.billing && data.data.billing.document_id,
          receipt_url: data.data.receipt_url || (data.data.billing && data.data.billing.receipt_url) || null,
          pdf_url: data.data.pdf_url || (data.data.billing && data.data.billing.pdf_url) || null
        }
        if (data.data.invoice_id) this.invoiceId = data.data.invoice_id
      }
      await this.loadOrder()
    },
    async callViewReceipt() {
      if (!this.canUseReceipt) return
      this.busy = true
      const path = this.receiptApiPath
      try {
        const res = await axios.get(path, { responseType: 'text' })
        this.saveStepResult(5, 'GET', path, res.status, {
          content_type: (res.headers && res.headers['content-type']) || 'text/html',
          bytes: typeof res.data === 'string' ? res.data.length : 0,
          receipt_url: this.paymentConfirm && this.paymentConfirm.receipt_url,
          pdf_url: this.paymentConfirm && this.paymentConfirm.pdf_url,
          preview: typeof res.data === 'string' ? res.data.slice(0, 500) + '…' : res.data
        }, true)
        window.open(
          URL.createObjectURL(new Blob([res.data], { type: 'text/html;charset=utf-8' })),
          '_blank',
          'noopener'
        )
        this.receiptOpened = true
      } catch (e) {
        this.saveStepResult(
          5,
          'GET',
          path,
          (e.response && e.response.status) || 0,
          (e.response && e.response.data) || { message: e.message },
          false
        )
      } finally {
        this.busy = false
      }
    },
    async callDownloadPdf() {
      if (!this.canUseReceipt) return
      this.busy = true
      const path = this.receiptPdfApiPath
      try {
        const res = await axios.get(path, { responseType: 'blob' })
        this.saveStepResult(5, 'GET', path, res.status, {
          content_type: (res.headers && res.headers['content-type']) || 'application/pdf',
          bytes: (res.data && res.data.size) || 0,
          receipt_url: this.paymentConfirm && this.paymentConfirm.receipt_url,
          pdf_url: this.paymentConfirm && this.paymentConfirm.pdf_url
        }, true)
        const a = document.createElement('a')
        a.href = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
        a.download = 'comprobante-invoice-' + this.invoiceId + '.pdf'
        document.body.appendChild(a)
        a.click()
        a.remove()
        this.receiptOpened = true
      } catch (e) {
        let data = (e.response && e.response.data) || { message: e.message }
        if (typeof Blob !== 'undefined' && data instanceof Blob) {
          try { data = JSON.parse(await data.text()) } catch (err) { data = { message: 'Error PDF' } }
        }
        this.saveStepResult(5, 'GET', path, (e.response && e.response.status) || 0, data, false)
      } finally {
        this.busy = false
      }
    },
    async callRating() {
      const ratings = []
      if (this.ratings.restaurant) {
        ratings.push({ type: 'restaurant', score: this.ratings.restaurant, comment: this.ratings.comment })
      }
      if (this.ratings.waiter) {
        ratings.push({ type: 'waiter', score: this.ratings.waiter, comment: this.ratings.comment })
      }
      await this.request(
        'post',
        'v1/table/order/' + this.orderId + '/rating',
        { ratings: ratings, guest_name: this.guest.name, invoice_id: this.invoiceId || null },
        6
      )
    },
    async runFullFlow() {
      if (!this.canRunFlow) {
        this.$swal({ icon: 'info', title: 'Arma la comanda en POS primero', timer: 2200, showConfirmButton: false })
        return
      }
      this.goStep(1)
      await this.callGetTable()
      this.goStep(2)
      await this.callPreview()
      this.goStep(3)
      await this.callCreateInvoice()
      this.goStep(4)
      if (this.invoiceId) await this.callCharge()
      this.goStep(5)
      if (this.canUseReceipt) await this.callViewReceipt()
      this.goStep(6)
      await this.callRating()
      this.$swal({ icon: 'success', title: 'Flujo completado', timer: 2000, showConfirmButton: false })
    }
  }
}
</script>

<style scoped>
.body { width: 100%; display: flex; min-height: 0; }
.body__containt {
  min-height: 0; width: 100%; display: flex; flex-direction: column;
  padding: 12px 20px 12px; background: #f4f5f7; overflow: hidden;
  height: 100%;
}
.page__title, .status-bar, .prep-card, .stepper { flex-shrink: 0; }
.page__title { display: flex; align-items: flex-start; gap: 10px; margin-bottom: 8px; }
.page__title h2 { margin: 0; color: #ff3128; font-size: 18px; font-weight: 600; }
.page__lead { margin: 2px 0 0; color: #666; font-size: 12px; max-width: 680px; line-height: 1.35; }
.page__lead a { color: #2d6cdf; font-weight: 600; }

.status-bar {
  display: flex; flex-wrap: wrap; gap: 8px; align-items: center;
  background: #fff; border: 1px solid #e6e6e6; border-radius: 10px; padding: 8px 12px; margin-bottom: 8px;
}
.status-item { display: flex; flex-direction: column; gap: 1px; min-width: 64px; }
.status-item--wide { min-width: 120px; flex: 1; }
.status-item span { font-size: 10px; color: #999; text-transform: uppercase; letter-spacing: .04em; }
.status-item strong { font-size: 12px; color: #222; }
.mono { font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 11px; word-break: break-all; }

.prep-card {
  background: #fff; border: 1px solid #e6e6e6; border-radius: 10px; padding: 8px 12px; margin-bottom: 8px;
}
.prep-row { display: flex; gap: 8px; align-items: flex-end; flex-wrap: wrap; }
.prep-row .field { min-width: 220px; flex: 1; margin-bottom: 0; }

.stepper { display: flex; gap: 6px; overflow-x: auto; margin-bottom: 8px; }
.stepper__item {
  display: flex; align-items: center; gap: 8px; border: 1px solid #e0e0e0; background: #fff;
  border-radius: 999px; padding: 5px 10px; cursor: pointer; white-space: nowrap; font-size: 12px; color: #666;
}
.stepper__item.active { border-color: #ff3128; background: #fff5f4; color: #ff3128; font-weight: 600; }
.stepper__item.done { border-color: #b8e0cb; color: #1b7a4a; }
.stepper__num {
  width: 20px; height: 20px; border-radius: 50%; background: #f0f0f0; display: inline-flex;
  align-items: center; justify-content: center; font-size: 11px; font-weight: 700;
}
.stepper__item.active .stepper__num { background: #ff3128; color: #fff; }
.stepper__item.done .stepper__num { background: #32ba7c; color: #fff; }

.step-panel {
  background: #fff; border: 1px solid #e6e6e6; border-radius: 14px;
  flex: 1 1 auto; min-height: 0; height: 100%;
  display: flex; flex-direction: column; overflow: hidden;
}
.step-head {
  display: flex; justify-content: space-between; gap: 12px; flex-wrap: wrap;
  padding: 10px 14px; border-bottom: 1px solid #f0f0f0; background: #fafbfc;
  flex-shrink: 0;
}
.step-name {
  margin: 0 0 2px; font-size: 11px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .04em; color: #ff3128;
}
.step-head h3 { margin: 0 0 4px; font-size: 15px; color: #1a1a1a; }
.step-desc { margin: 0; font-size: 12px; color: #666; line-height: 1.35; max-width: 640px; }
.api-badge {
  display: flex; align-items: center; gap: 8px; align-self: flex-start;
  background: #fff; border: 1px solid #e8e8e8; border-radius: 10px; padding: 6px 8px; max-width: 100%;
}
.api-badge__method {
  font-size: 11px; font-weight: 800; padding: 3px 8px; border-radius: 6px; color: #fff; flex-shrink: 0;
}
.m-get { background: #2d6cdf; }
.m-post { background: #32ba7c; }
.api-badge__path {
  font-size: 11px; color: #444; word-break: break-all;
  font-family: ui-monospace, Menlo, Consolas, monospace;
}

.step-layout {
  display: grid; grid-template-columns: minmax(360px, 440px) minmax(0, 1fr);
  flex: 1 1 auto; min-height: 0; overflow: hidden; align-items: stretch;
}
.step-layout > * { min-height: 0; min-width: 0; }
.try {
  padding: 12px 14px 16px; border-right: 1px solid #f0f0f0; background: #fff;
  min-height: 0; overflow-x: hidden; overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
.panel__title {
  margin: 0 0 8px; font-size: 11px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .05em; color: #999;
}

.reply { display: flex; flex-direction: column; min-width: 0; min-height: 0; overflow: hidden; background: #fafbfc; }
.reply__tabs {
  display: flex; gap: 0; border-bottom: 1px solid #eee; background: #fff; flex-shrink: 0;
}
.reply__tabs button {
  flex: 1; border: none; background: transparent; padding: 12px 10px; font-size: 13px;
  font-weight: 600; color: #888; cursor: pointer; border-bottom: 2px solid transparent;
}
.reply__tabs button.active { color: #ff3128; border-bottom-color: #ff3128; background: #fff; }
.reply__body { flex: 1; overflow: auto; min-height: 0; max-height: none; padding: 14px 16px; }

.result-banner {
  display: flex; flex-direction: column; gap: 4px; margin-bottom: 12px;
  padding: 10px 12px; border-radius: 10px; font-size: 12px; line-height: 1.35;
}
.result-banner strong { font-size: 13px; }
.result-banner.is-ok { background: #eaf8f0; border: 1px solid #b7e4c7; color: #1b7a4a; }
.result-banner.is-err { background: #fdecec; border: 1px solid #f5c2c2; color: #b42318; }
.result-banner.is-empty { background: #fff8e8; border: 1px solid #ffe0a3; color: #6d4c00; }
.result-banner span { opacity: .9; word-break: break-all; }

.guide__group { margin-bottom: 14px; }
.guide__group h5 {
  margin: 0 0 8px; font-size: 11px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .04em; color: #ff3128;
}
.guide__row {
  display: grid; grid-template-columns: minmax(130px, 170px) 1fr; gap: 10px;
  padding: 8px 0; border-bottom: 1px solid #eee; align-items: start;
}
.guide__row--req { grid-template-columns: minmax(130px, 170px) 1fr; }
.guide__row code {
  font-size: 11px; background: #fff; border: 1px solid #e8e8e8; padding: 3px 6px;
  border-radius: 4px; color: #333; word-break: break-all;
}
.guide__meta { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.guide__val { font-size: 13px; color: #111; word-break: break-word; font-weight: 600; }
.guide__val.muted { color: #bbb; font-weight: 500; }
.guide__desc { font-size: 12px; color: #666; line-height: 1.35; }

.json-box__head { margin-bottom: 8px; }
.pill {
  display: inline-block; background: #222; color: #9fef9f; font-size: 11px;
  padding: 4px 8px; border-radius: 999px;
}
.json-box pre {
  margin: 0; background: #141414; color: #9fef9f; padding: 12px; border-radius: 10px;
  font-size: 11px; white-space: pre-wrap; word-break: break-word; line-height: 1.45;
}
.empty-result { color: #999; font-size: 13px; text-align: center; padding: 40px 12px; }
.request-json { margin-top: 18px; padding-top: 14px; border-top: 1px solid #eee; }
.request-json h5 {
  margin: 0 0 6px; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; color: #888;
}
.request-json__meta {
  margin: 0 0 8px; font-size: 12px; color: #555; font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  word-break: break-all;
}
.request-json .empty-result { padding: 16px 12px; }

.field { display: flex; flex-direction: column; gap: 3px; font-size: 12px; color: #666; margin-bottom: 8px; }
.field--sm { max-width: 120px; }
.field input, .field select {
  border: 1px solid #ddd; border-radius: 8px; padding: 7px 10px; font-size: 13px; background: #fff;
}
.check { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #444; margin-bottom: 8px; }
.check--item {
  align-items: flex-start; padding: 6px 8px; border: 1px solid #eee; border-radius: 8px;
  background: #fafafa; margin-bottom: 4px;
}
.check--item em { display: block; font-style: normal; color: #888; font-size: 12px; margin-top: 2px; }
.check--item.is-reserved { opacity: .65; background: #f5f5f5; }
.items-pick { margin-bottom: 8px; max-height: 140px; overflow: auto; }
.items-pick__title { margin: 0 0 6px; font-size: 12px; font-weight: 600; color: #333; }
.guest-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 8px; }
.guest-grid .field { margin-bottom: 0; }

.btn-row { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin-top: 6px; }
.btn {
  border: none; border-radius: 9px; padding: 8px 12px; background: #2d6cdf; color: #fff;
  font-size: 13px; font-weight: 600; cursor: pointer;
}
.btn:disabled { opacity: .5; cursor: not-allowed; }
.btn--accent { background: #32ba7c; }
.btn--ghost { background: #fff; color: #555; border: 1px solid #ddd; }
.btn--next { background: #ff3128; margin-left: auto; }

.tip { margin: 0 0 8px; font-size: 12px; color: #888; line-height: 1.4; }
.tip--ok { color: #1b7a4a; }
.tip a { color: #2d6cdf; font-weight: 600; }

@media (max-width: 1100px) {
  .step-layout { grid-template-columns: minmax(320px, 380px) minmax(0, 1fr); }
}
@media (max-width: 960px) {
  .step-layout { grid-template-columns: 1fr; overflow: auto; }
  .try { border-right: none; border-bottom: 1px solid #f0f0f0; max-height: none; overflow: visible; }
  .reply { overflow: visible; }
  .reply__body { max-height: none; }
  .guest-grid { grid-template-columns: 1fr; }
  .guide__row { grid-template-columns: 1fr; }
  .btn--next { margin-left: 0; }
}
</style>
